import { NavigationBarContainer } from "@atlas-int/tokens/layout";
import {
  Bar,
  CenterBar,
  HamburgerMenuButton,
  MenuHeaderContainer,
  ContentPageMenuHeaderContainer,
  MenuBodyContainer,
  Icon,
} from "./styles";
import {
  Link,
  LinkProps,
  To,
  useLocation,
  useSearchParams,
} from "react-router";
import { logos } from "../../assets/icons";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import VizTypeSelectorButton from "../buttons/VizTypeSelectorButton";
import { ProductClass } from "@atlas-int/data/types";
import { VizType, getLocationLevelFromStringLocationId, unidentifiedServicesCountryId, useCountriesMetadata, worldGroupDatum } from "@atlas-int/data";
import { useCurrentVizType } from "../../hooks/useCurrentVizType";
import GlossaryModalButton from "../buttons/GlossaryModalButton";
import DataNotesModalButton from "../buttons/DataNotesModalButton";
import { LocationLevel } from "@atlas-int/data/graphql";
import HamburgerMenu from "./HamburgerMenu";
import { getReferenceLocation } from "@atlas-int/viz/geomap";
import SubregionMapModalButton from "../buttons/SubregionMapModalButton";
import GuideModalButton from "../buttons/GuideModalButton";
import DownloadModalButton from "../buttons/DownloadModalButton";
import ShareModalButton from "../buttons/ShareModalButton";
import { mergeParams, useExploreParams } from "../../visualization/paramSchemas";
import { useOldCountryIds } from "../../routing/Utils";
import ExploreFurther from "./ExploreFurther";
import {
  MenuHeaderGrowthLabIcon,
  MenuRow,
  MenuRowIcon,
  MenuRowLabel,
  NotAvailableTooltip,
  OpenPanelArrow,
  OpenPanelArrowButtonContainer,
  OpenPanelArrowContainer,
  OptionsList,
  OptionsListRow,
  VizTypeList,
} from "./Components";
import { DisabledOverlay } from "@atlas-int/ui";
import { googleAnalyticsCustomEvent } from "../../sharedUtilities/analytics";
import { MenuHeaderActionRow } from "./styles";

const defaultDisabledMessage =
  "Complexity visualizations are not available for regions, subregions, and Services Partners";

/**
 * Wrapper for disabled viz-type menu items.
 * Uses DisabledOverlay with a custom fixed-position tooltip that appears
 * to the right of the navigation bar.
 */
const DisabledVizTypeOption = ({
  children,
  message,
}: {
  children: React.ReactNode;
  message?: string;
}) => {
  const displayMessage = message ?? defaultDisabledMessage;

  return (
    <DisabledOverlay
      disabled
      dimChildren={false}
      renderTooltip={(containerRef) => {
        const node = containerRef.current;
        if (!node) return null;
        const { top, height } = node.getBoundingClientRect();
        const positionFromTop = top + height / 2;
        return (
          <NotAvailableTooltip style={{ top: `${positionFromTop}px` }}>
            {displayMessage}
          </NotAvailableTooltip>
        );
      }}
    >
      {children}
    </DisabledOverlay>
  );
};

interface ValidateNavigationInput {
  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>;
  currentPathName: string;
  toPath: To;
}

const validateNavigation = ({
  event,
  currentPathName,
  toPath,
}: ValidateNavigationInput) => {
  if (currentPathName === toPath) {
    event.preventDefault();
  }
};

function ValidatedNavigationLink({ to, children }: LinkProps): ReactElement {
  const { pathname: currentPathName } = useLocation();
  return (
    <Link
      to={to}
      onClick={(event) =>
        validateNavigation({ event, currentPathName, toPath: to })
      }
    >
      {children}
    </Link>
  );
}

export enum NavigationBarPageType {
  ContentPage = "ContentPage",
}

export const navigationBarElementId = "navigation-bar-container";

const NavigationBar = ({ pageType }: { pageType?: NavigationBarPageType }) => {
  // redirect url if old ids are present
  useOldCountryIds();
  const vizType = useCurrentVizType();
  const [query] = useExploreParams();
  const [search] = useSearchParams();

  const existingQuery = {
    ...Object.fromEntries(
      Array.from(search.entries()).filter(
        ([, v]) => v !== "undefined" && v !== undefined,
      ),
    ),
  };
  if (query.view && query.view?.length > 0) {
    existingQuery.view = query.view;
  }
  const {
    importer,
    exporter,
    productClass,
  } = query;
  const [isNavigationBarOpen, setIsNavigationBarOpen] =
    useState<boolean>(false);
  let [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [navigationBarOpenedOnce, setNavigationBarOpenedOnce] =
    useState<boolean>(false);
  const [navigationBarContainerClassName, setNavigationBarContainerClassName] =
    useState<string | undefined>(undefined);

  const primaryOptionsRowId = "options-row-primary";

  let hamburgerMenuContents: React.ReactElement | null;
  if (isMenuOpen) {
    hamburgerMenuContents = (
      <HamburgerMenu
        closeMenu={() => {
          setIsMenuOpen(false);
          setIsNavigationBarOpen(false);
        }}
      />
    );
  } else {
    hamburgerMenuContents = null;
  }

  const handleOpenPanelArrow = () => {
    if (!navigationBarOpenedOnce) setNavigationBarOpenedOnce(true);
    const newIsNavigationBarOpen = !isNavigationBarOpen;

    // Add Google Analytics event when opening the panel
    if (!isNavigationBarOpen) {
      googleAnalyticsCustomEvent("leftnav", "click", "menu", "explore");
    }

    /*
        NOTE:

        This is a really clunky way of handling the transition for the primary options row,
        i.e., the buttons for Guide, Download, and Data Alerts.

        The problem:

        When the navigation bar is collapsed, these 3 option buttons remain visible; when the
        bar is expanded, these 3 option buttons are still visible, accompanied by 3 additional
        buttons (subregion map, glossary, and share).

        The 3 new options are newly-rendered elements, and thus trigger the delayed visibility
        animation; however, the 3 primary options are not newly rendered, and there is no clean way
        in CSS to re-trigger the delayed visibility animation for those options.

        The result is that in the expansion and closing animation, the 3 primary option buttons
        reflow between horizontal and vertical, which is visually messy.

        The following solution uses JavaScript to force a temporary opacity switch for
        the primary options row, just long enough to hide the reflow of the elements. 

        */
    const primaryOptionsRow = document.querySelector(
      `#${primaryOptionsRowId}`,
    ) as HTMLElement;
    if (primaryOptionsRow) {
      primaryOptionsRow.style.opacity = "0";
      primaryOptionsRow.style.animation = "none";

      setTimeout(() => {
        primaryOptionsRow.style.opacity = "1";
      }, 300);
    }

    // When closing the sidebar, also close the hamburger menu
    if (!newIsNavigationBarOpen) {
      setIsMenuOpen(false);
    }

    setIsNavigationBarOpen(newIsNavigationBarOpen);
  };

  const handleHamburgerMenuClick = () => {
    if (!isMenuOpen) {
      setIsMenuOpen(true);
      setIsNavigationBarOpen(true);
    } else {
      setIsMenuOpen(!isMenuOpen);
    }
  };

  useEffect(() => {
    let newNavigationBarContainerClassName;
    if (isNavigationBarOpen) {
      newNavigationBarContainerClassName = "expanded";
    } else if (!isNavigationBarOpen && navigationBarOpenedOnce) {
      newNavigationBarContainerClassName = "closed";
    } else {
      newNavigationBarContainerClassName = undefined;
    }
    setNavigationBarContainerClassName(newNavigationBarContainerClassName);
  }, [isNavigationBarOpen, navigationBarOpenedOnce]);

  // Pre-fetch countries metadata (used by child components)
  useCountriesMetadata();

  if (pageType === NavigationBarPageType.ContentPage) {
    const handleContentPageHamburgerMenuClick = () => {
      if (!isMenuOpen) {
        setIsMenuOpen(true);
        setIsNavigationBarOpen(true);
      } else {
        setIsMenuOpen(false);
        setIsNavigationBarOpen(false);
      }
    };

    return (
      <NavigationBarContainer
        id={navigationBarElementId}
        className={`${navigationBarContainerClassName} contentPage`}
      >
        <ContentPageMenuHeaderContainer>
          <HamburgerMenuButton onClick={handleContentPageHamburgerMenuClick}>
            Menu
            <Icon>
              <Bar style={{ opacity: isMenuOpen ? 0 : undefined }} />
              <CenterBar className={isMenuOpen ? "close__menu" : undefined} />
              <Bar style={{ opacity: isMenuOpen ? 0 : undefined }} />
            </Icon>
          </HamburgerMenuButton>
        </ContentPageMenuHeaderContainer>
        <MenuBodyContainer>{hamburgerMenuContents}</MenuBodyContainer>
      </NavigationBarContainer>
    );
  } else {
    let disabledButtonClassName;
    let referenceLocation = getReferenceLocation({
      exporter,
      importer,
    });
    let referenceLocationLevel =
      getLocationLevelFromStringLocationId(referenceLocation);

    // When exporter is World and importer is a country, complexity viz
    // links should navigate with the country as the exporter.
    const exporterIsWorld = exporter === worldGroupDatum.groupId;
    const importerIsCountry =
      getLocationLevelFromStringLocationId(importer) === LocationLevel.Country;
    const complexityVizCountryOverride =
      exporterIsWorld && importerIsCountry ? importer : undefined;

    // Query override that swaps the country into the exporter slot.
    // Used by country-specific viz links (ProductSpace, Feasibility, MarketShare).
    const countryExporterQuery = complexityVizCountryOverride
      ? { ...existingQuery, exporter: complexityVizCountryOverride, importer: worldGroupDatum.groupId }
      : existingQuery;

    // Disable complex visualizations for groups and Services Partners,
    // unless we have a country override from the importer.
    if (
      !complexityVizCountryOverride &&
      (referenceLocationLevel === LocationLevel.Group ||
       referenceLocation === unidentifiedServicesCountryId)
    ) {
      disabledButtonClassName = "disabledVisOption";
    } else {
      disabledButtonClassName = null;
    }

    const isHS22 = productClass === ProductClass.HS22Products;

    const productSpaceEntry = (
      <>
        <MenuRowIcon>
          <VizTypeSelectorButton vizType={VizType.ProductSpace} />
        </MenuRowIcon>
        {isNavigationBarOpen && (
          <MenuRowLabel>
            Depicts the connectedness between products, based on the similarity
            of know-how required to produce them; helps to reveal paths to
            diversify a country's economy based on the connectedness of its
            existing knowhow.
          </MenuRowLabel>
        )}
      </>
    );
    const feasibilityEntry = (
      <>
        <MenuRowIcon>
          <VizTypeSelectorButton vizType={VizType.Feasibility} />
        </MenuRowIcon>
        <MenuRowLabel>
          Displays a country's opportunities for export diversification based on
          what it currently exports.
        </MenuRowLabel>
      </>
    );
    let productSpaceVisOptionMenuItem: ReactNode;
    let feasibilityVisOptionMenuItem: ReactNode;

    if (disabledButtonClassName) {
      productSpaceVisOptionMenuItem = (
        <DisabledVizTypeOption>
          <MenuRow className={disabledButtonClassName}>
            {productSpaceEntry}
          </MenuRow>
        </DisabledVizTypeOption>
      );
      feasibilityVisOptionMenuItem = (
        <DisabledVizTypeOption>
          <MenuRow className={disabledButtonClassName}>
            {feasibilityEntry}
          </MenuRow>
        </DisabledVizTypeOption>
      );
    } else {
      productSpaceVisOptionMenuItem = (
        <ValidatedNavigationLink
          onClick={() =>
            googleAnalyticsCustomEvent(
              "leftnav",
              "click",
              "productspace",
              "explore",
            )
          }
          to={`/explore/productspace?${mergeParams(VizType.ProductSpace, countryExporterQuery)}`}
        >
          <MenuRow
            className={vizType === VizType.ProductSpace ? "selected" : ""}
          >
            {productSpaceEntry}
          </MenuRow>
        </ValidatedNavigationLink>
      );

      feasibilityVisOptionMenuItem = (
        <ValidatedNavigationLink
          onClick={() =>
            googleAnalyticsCustomEvent(
              "leftnav",
              "click",
              "growthopp",
              "explore",
            )
          }
          to={`/explore/feasibility?${mergeParams(VizType.Feasibility, {
            ...countryExporterQuery,
            ...(isHS22 ? { productClass: ProductClass.HS92Products } : {}),
          })}`}
        >
          <MenuRow
            className={vizType === VizType.Feasibility ? "selected" : ""}
          >
            {feasibilityEntry}
          </MenuRow>
        </ValidatedNavigationLink>
      );
    }

    let moreOptionsRow: React.ReactNode;
    if (isNavigationBarOpen === true) {
      moreOptionsRow = (
        <OptionsListRow className={navigationBarContainerClassName}>
          <SubregionMapModalButton
            menuOptionClassName={navigationBarContainerClassName}
            isNavigationBarOpen={isNavigationBarOpen}
          />
          <ShareModalButton
            menuOptionClassName={navigationBarContainerClassName}
            isNavigationBarOpen={isNavigationBarOpen}
          />
          <GlossaryModalButton
            menuOptionClassName={navigationBarContainerClassName}
            isNavigationBarOpen={isNavigationBarOpen}
          />
        </OptionsListRow>
      );
    } else {
      moreOptionsRow = null;
    }
    return (
      <NavigationBarContainer
        id={navigationBarElementId}
        className={navigationBarContainerClassName}
      >
        <MenuHeaderContainer>
          <MenuRow>
            {isNavigationBarOpen ? (
              <MenuHeaderGrowthLabIcon className="fadeIn">
                <Link to={"/"}>
                  <img
                    src={logos.glLogoWhite}
                    alt="The Atlas of Economic Complexity"
                  />
                </Link>
              </MenuHeaderGrowthLabIcon>
            ) : (
              <MenuHeaderGrowthLabIcon className="collapsed">
                <Link to={"/"}>
                  <img
                    src={logos.glAtlasFavicon}
                    alt="The Atlas of Economic Complexity"
                  />
                </Link>
              </MenuHeaderGrowthLabIcon>
            )}
          </MenuRow>

          {isNavigationBarOpen ? (
            <MenuHeaderActionRow>
              <HamburgerMenuButton onClick={handleHamburgerMenuClick}>
                Menu
                <Icon>
                  <Bar style={{ opacity: isMenuOpen ? 0 : undefined }} />
                  <CenterBar className={isMenuOpen ? "close__menu" : undefined} />
                  <Bar style={{ opacity: isMenuOpen ? 0 : undefined }} />
                </Icon>
              </HamburgerMenuButton>
              <OpenPanelArrowContainer
                className={navigationBarContainerClassName}
                onClick={handleOpenPanelArrow}
              >
                <OpenPanelArrowButtonContainer>
                  <OpenPanelArrow className="closeArrow" />
                </OpenPanelArrowButtonContainer>
              </OpenPanelArrowContainer>
            </MenuHeaderActionRow>
          ) : (
            <HamburgerMenuButton onClick={handleHamburgerMenuClick}>
              Menu
              <Icon>
                <Bar style={{ opacity: isMenuOpen ? 0 : undefined }} />
                <CenterBar className={isMenuOpen ? "close__menu" : undefined} />
                <Bar style={{ opacity: isMenuOpen ? 0 : undefined }} />
              </Icon>
            </HamburgerMenuButton>
          )}
        </MenuHeaderContainer>
        <MenuBodyContainer>
          {hamburgerMenuContents}
          {!isNavigationBarOpen && (
            <OpenPanelArrowContainer
              onClick={handleOpenPanelArrow}
            >
              <OpenPanelArrowButtonContainer>
                <OpenPanelArrow className="openArrow" />
              </OpenPanelArrowButtonContainer>
            </OpenPanelArrowContainer>
          )}
          <VizTypeList>
            <div className="analyzeTradeFlows" style={{ overflow: "hidden" }}>
              <ValidatedNavigationLink
                onClick={() =>
                  googleAnalyticsCustomEvent(
                    "leftnav",
                    "click",
                    "treemap",
                    "explore",
                  )
                }
                to={`/explore/treemap?${mergeParams(VizType.Tree, existingQuery)}`}
              >
                <MenuRow
                  className={vizType === VizType.Tree ? "selected" : ""}
                >
                  <MenuRowIcon>
                    <VizTypeSelectorButton vizType={VizType.Tree} />
                  </MenuRowIcon>
                  <MenuRowLabel>
                    Displays the breakdown of exports or imports by location or
                    product, in a given year.
                  </MenuRowLabel>
                </MenuRow>
              </ValidatedNavigationLink>

              <ValidatedNavigationLink
                onClick={() =>
                  googleAnalyticsCustomEvent(
                    "leftnav",
                    "click",
                    "geo",
                    "explore",
                  )
                }
                to={`/explore/geomap?${mergeParams(VizType.Geo, existingQuery)}`}
              >
                <MenuRow
                  className={vizType === VizType.Geo ? "selected" : ""}
                >
                  <MenuRowIcon>
                    <VizTypeSelectorButton vizType={VizType.Geo} />
                  </MenuRowIcon>
                  <MenuRowLabel>
                    A world map depicting the value of trade flows, by location
                    or by product, in a given year.
                  </MenuRowLabel>
                </MenuRow>
              </ValidatedNavigationLink>

              <ValidatedNavigationLink
                onClick={() =>
                  googleAnalyticsCustomEvent(
                    "leftnav",
                    "click",
                    "stack",
                    "explore",
                  )
                }
                to={`/explore/overtime?${mergeParams(
                  VizType.OverTime,
                  existingQuery,
                )}`}
              >
                <MenuRow
                  className={
                    vizType === VizType.OverTime ? "selected" : ""
                  }
                >
                  <MenuRowIcon>
                    <VizTypeSelectorButton vizType={VizType.OverTime} />
                  </MenuRowIcon>
                  <MenuRowLabel>
                    Displays the breakdown of exports or imports, by location
                    or by product, over a specified time period.
                  </MenuRowLabel>
                </MenuRow>
              </ValidatedNavigationLink>
              <ValidatedNavigationLink
                onClick={() =>
                  googleAnalyticsCustomEvent(
                    "leftnav",
                    "click",
                    "marketshare",
                    "explore",
                  )
                }
                to={`/explore/marketshare?${mergeParams(
                  VizType.MarketShare,
                  countryExporterQuery,
                )}`}
              >
                <MenuRow
                  className={
                    vizType === VizType.MarketShare ? "selected" : ""
                  }
                >
                  <MenuRowIcon>
                    <VizTypeSelectorButton vizType={VizType.MarketShare} />
                  </MenuRowIcon>
                  <MenuRowLabel>
                    Displays a location's share of the global market across 10
                    product sectors.
                  </MenuRowLabel>
                </MenuRow>
              </ValidatedNavigationLink>
            </div>
            <div
              className="analyzeCountryComplexity"
              style={{ overflow: "hidden" }}
            >
              {productSpaceVisOptionMenuItem}
              {feasibilityVisOptionMenuItem}
            </div>
          </VizTypeList>
          {isNavigationBarOpen && <ExploreFurther />}
          <OptionsList className="learnMoreAndShare">
            <OptionsListRow id={primaryOptionsRowId} className={navigationBarContainerClassName}>
              <GuideModalButton
                menuOptionClassName={navigationBarContainerClassName}
                isNavigationBarOpen={isNavigationBarOpen}
              />
              <DownloadModalButton
                menuOptionClassName={navigationBarContainerClassName}
                isNavigationBarOpen={isNavigationBarOpen}
              />
              <DataNotesModalButton
                menuOptionClassName={navigationBarContainerClassName}
                isNavigationBarOpen={isNavigationBarOpen}
              />
            </OptionsListRow>
            {moreOptionsRow}
          </OptionsList>
        </MenuBodyContainer>
      </NavigationBarContainer>
    );
  }
};

export default NavigationBar;
